import { useState } from "react";
import { motion } from "framer-motion";
import { Reveal, PageHero } from "@/components/site/Reveal";
import { Lightbox } from "@/components/site/Lightbox";
import { imgUrl } from "@/lib/api";
import { Html, Btn, TextLink, Head, Img, bg } from "./Parts";

const HeroFull = ({ d }) => (
  <section className="relative min-h-[100svh] overflow-hidden bg-ink grain flex flex-col justify-end" data-testid="block-hero-full">
    {d.image && <motion.img src={imgUrl(d.image)} alt={d.image_alt || ""} className="absolute inset-0 w-full h-full object-cover"
      initial={{ scale: 1.08, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ scale: { duration: 16, ease: "easeOut" }, opacity: { duration: 1.6 } }} />}
    <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-ink/55 to-transparent" />
    <div className="absolute inset-y-0 right-0 w-px bg-seta/20 hidden lg:block" />
    <p className="hidden xl:block absolute right-0 top-1/2 origin-center -translate-y-1/2 translate-x-[calc(50%-2.6rem)] rotate-90 text-[10px] uppercase tracking-[0.4em] text-seta/65 whitespace-nowrap">Studio Fotografico Santarelli — Archivio dal 1982</p>

    <div className="relative z-[2] w-full">
      <motion.div className="bg-seta max-w-[min(94%,46rem)] ml-0 sm:ml-10 lg:ml-16 px-6 sm:px-12 pt-10 pb-11 sm:pt-14 sm:pb-14"
        initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35, duration: 1.3, ease: [0.22, 1, 0.36, 1] }}>
        {d.eyebrow && <p className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[10.5px] uppercase tracking-[0.26em] text-accent mb-8" data-testid="hero-eyebrow"><span className="w-8 h-px bg-accent" />{d.eyebrow}</p>}
        <Html inline as="h1" html={d.title} className="font-serif font-light text-[40px] sm:text-[58px] lg:text-[68px] leading-[0.98] tracking-[-0.035em] text-ink [&_em]:italic [&_em]:text-accent" />
        {d.subtitle && <p className="mt-7 text-[15px] sm:text-[16.5px] font-light text-stoneish leading-[1.8] max-w-xl" data-testid="hero-subtitle">{d.subtitle}</p>}
        <div className="mt-10 flex flex-wrap gap-3 sm:gap-4"><Btn text={d.button_text} to={d.button_link} testid="hero-button" /><Btn text={d.button2_text} to={d.button2_link} outline testid="hero-button-2" /></div>
      </motion.div>
      <motion.div className="flex flex-wrap items-center gap-x-10 gap-y-3 px-6 sm:px-10 lg:px-16 py-6 text-seta/85 text-[10.5px] uppercase tracking-[0.24em] border-t border-seta/15 bg-ink/45 backdrop-blur-sm"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1, duration: 1.2 }} data-testid="hero-meta">
        <span className="text-accent-light">1982</span><span>Sassa Scalo · L'Aquila</span><span className="hidden sm:inline">Fotografia documentaria</span>
        {d.intro && <span className="normal-case tracking-normal text-[13px] text-seta/70 font-light">{d.intro}</span>}
      </motion.div>
    </div>
  </section>
);

const HeroSplit = ({ d }) => (
  <header className="container-x pt-32 sm:pt-40 pb-16 grid grid-cols-12 gap-x-6 gap-y-12 items-end" data-testid="block-hero-split">
    <Reveal className="col-span-12 lg:col-span-6">
      {d.eyebrow && <p className="eyebrow mb-8 flex items-center gap-4"><span className="rule-gold" />{d.eyebrow}</p>}
      <Html inline as="h1" html={d.title} className="font-serif font-light text-[46px] sm:text-6xl lg:text-[76px] leading-[1.01] tracking-[-0.03em] [&_em]:italic [&_em]:text-oliva" data-testid="service-hero-title" />
      {d.intro && <p className="mt-8 text-[15.5px] font-light text-stoneish leading-[1.85] max-w-lg">{d.intro}</p>}
      <div className="mt-10 flex flex-wrap gap-4"><Btn text={d.button_text} to={d.button_link} testid="hero-button" /><Btn text={d.button2_text} to={d.button2_link} outline testid="hero-button-2" /></div>
    </Reveal>
    <Reveal delay={0.18} className="col-span-12 sm:col-span-10 sm:col-start-3 lg:col-span-5 lg:col-start-8">
      {d.image ? <div className="overflow-hidden img-zoom"><Img src={d.image} alt={d.image_alt} eager className="w-full object-cover aspect-[4/5] max-h-[80vh]" /></div>
        : <div className="w-full bg-sabbia border border-line flex items-end p-8 aspect-[4/5] max-h-[80vh]" aria-hidden="true"><span className="font-serif italic font-light text-5xl text-pietra">{d.eyebrow}</span></div>}
    </Reveal>
  </header>
);

export const Hero = ({ d }) => (d.variant === "full" ? <HeroFull d={d} /> : d.variant === "split" ? <HeroSplit d={d} />
  : (
    <>
      <PageHero eyebrow={d.eyebrow} title={<Html inline as="span" html={d.title} className="[&_em]:italic" />} intro={d.intro} />
      {d.image && d.image_layout !== "hidden" && (
        <Reveal className="container-x pb-10 sm:pb-16">
          <div className="overflow-hidden img-zoom"><Img src={d.image} alt={d.image_alt} eager className="w-full object-cover aspect-[16/9] max-h-[70vh]" /></div>
        </Reveal>
      )}
    </>
  ));

export const TextImage = ({ d }) => {
  const wide = d.layout === "image_wide";
  const left = d.image_side === "left";
  const text = (
    <Reveal key="t" delay={wide ? 0.18 : 0} className={wide ? `col-span-12 lg:col-span-4 lg:self-end ${left ? "lg:col-start-9" : "lg:col-start-1 lg:row-start-1"}` : `col-span-12 lg:col-span-6 ${left ? "lg:col-start-7" : ""}`}>
      {d.eyebrow && <p className="eyebrow mb-8 flex items-center gap-4"><span className="rule-gold" />{d.eyebrow}</p>}
      <Html inline as="p" html={d.lead} className={`font-serif font-light leading-[1.22] tracking-[-0.025em] whitespace-pre-line [&_em]:italic [&_em]:text-oliva ${wide ? "text-[26px] sm:text-[32px]" : "text-[30px] sm:text-[44px]"}`} />
      <Html html={d.body} className="mt-8 max-w-xl" />
      {d.points?.length > 0 && <ul className="mt-10 grid sm:grid-cols-2 gap-x-8 max-w-xl" data-testid="text-image-points">{d.points.map((p, k) => <li key={k} className="py-4 border-t border-line text-[14.5px] text-ink flex gap-4"><span className="text-oro text-[11px] pt-1 tracking-[0.1em]">{String(k + 1).padStart(2, "0")}</span>{p.title}</li>)}</ul>}
      {d.button_text && <div className="mt-10"><TextLink text={d.button_text} to={d.button_link} testid="text-image-link" /></div>}
    </Reveal>
  );
  const img = d.image && (
    <Reveal key="i" delay={wide ? 0 : 0.18} className={wide ? `col-span-12 lg:col-span-7 ${left ? "" : "lg:col-start-6"}` : `col-span-10 col-start-3 sm:col-span-8 sm:col-start-5 lg:col-span-5 ${left ? "lg:col-start-1 lg:row-start-1" : "lg:col-start-8"}`}>
      <div className="overflow-hidden img-zoom"><Img src={d.image} alt={d.image_alt} className={`w-full object-cover ${wide ? "aspect-[3/2]" : "aspect-[4/5]"}`} /></div>
    </Reveal>
  );
  return <section className={`${bg(d)} ${wide ? "py-24 sm:py-32" : "py-24 sm:py-36"}`} data-testid="block-text-image"><div className={`container-x grid grid-cols-12 gap-x-6 lg:gap-x-10 gap-y-12 ${wide ? "" : "lg:items-center"}`}>{wide && left ? [img, text] : [text, img]}</div></section>;
};

export const RichText = ({ d }) => (
  <section className={`${bg(d)} py-20 sm:py-28`} data-testid="block-rich-text">
    <div className="container-x grid grid-cols-12 gap-6">
      <Reveal className={`col-span-12 ${d.width === "wide" ? "" : "lg:col-span-8 lg:col-start-2"}`}>
        {d.eyebrow && <p className="eyebrow mb-7 flex items-center gap-4"><span className="rule-gold" />{d.eyebrow}</p>}
        <Html inline as="h2" html={d.title} className="font-serif font-light text-4xl sm:text-[52px] leading-[1.06] tracking-[-0.025em] mb-10 [&_em]:italic [&_em]:text-oliva" />
        <Html html={d.body} />
      </Reveal>
    </div>
  </section>
);

const Items = ({ d }) => {
  if (d.style === "steps") return d.items.map((it, k) => (
    <Reveal key={k} delay={k * 0.1} className="grid grid-cols-12 gap-4 py-8 border-t border-line first:border-t-0 first:pt-0">
      <span className="col-span-2 text-[11px] tracking-[0.14em] text-oro pt-3">{String(k + 1).padStart(2, "0")}</span><h3 className="col-span-10 sm:col-span-4 font-serif font-light text-[28px] leading-tight">{it.title}</h3>
      <p className="col-span-10 col-start-3 sm:col-span-6 text-[15px] font-light text-stoneish leading-[1.85]">{it.text}</p>
    </Reveal>));
  if (d.style === "numbered") return <ol>{d.items.map((it, k) => <li key={k} className="flex gap-6 py-4 border-b border-line text-[15.5px] font-light transition-colors duration-500 hover:text-oliva"><span className="text-oro text-[11px] tracking-[0.14em] pt-1.5">{String(k + 1).padStart(2, "0")}</span>{it.title}</li>)}</ol>;
  return <ul className="grid sm:grid-cols-2 gap-x-10" data-testid="service-includes">{d.items.map((it, k) => <li key={k} className="py-4 border-b border-line text-[15.5px] font-light">{it.title}</li>)}</ul>;
};

export const List = ({ d }) => {
  if (d.style === "columns") return (
    <section className={`${bg(d)} py-24 sm:py-32`} data-testid="block-list"><div className="container-x">
      <Head d={d} />
      <div className="grid md:grid-cols-4 gap-x-10 gap-y-14">{d.items.map((it, k) => (
        <Reveal key={k} delay={k * 0.1} className={`border-t border-oro/40 pt-8 ${k % 2 ? "md:mt-14" : ""}`}>
          <h3 className="font-serif font-light text-[32px] leading-tight tracking-[-0.02em]">{it.title}</h3>
          <p className="mt-5 text-[15px] font-light text-stoneish leading-[1.85]">{it.text}</p></Reveal>))}</div>
    </div></section>);
  const steps = d.style === "steps";
  return (
    <section className={`${bg(d)} py-24 sm:py-32`} data-testid="block-list">
      <div className="container-x grid grid-cols-12 gap-x-6 gap-y-12">
        <Reveal className={`col-span-12 ${steps ? "lg:col-span-4 lg:sticky lg:top-32 lg:self-start" : "lg:col-span-5"}`}>
          {d.eyebrow && <p className="eyebrow mb-8 flex items-center gap-4"><span className="rule-gold" />{d.eyebrow}</p>}
          <Html inline as={steps ? "h2" : "p"} html={d.title} className={`font-serif font-light [&_em]:italic [&_em]:text-oliva ${steps ? "text-4xl sm:text-[52px] leading-[1.05] tracking-[-0.025em]" : "text-[28px] sm:text-[36px] leading-[1.3] tracking-[-0.02em]"}`} />
          {d.intro && <p className="mt-7 text-[15px] font-light text-stoneish leading-[1.85] max-w-sm">{d.intro}</p>}
        </Reveal>
        <Reveal delay={0.15} className={`col-span-12 ${steps ? "lg:col-span-7 lg:col-start-6" : "lg:col-span-6 lg:col-start-7 lg:mt-4"}`}>
          {d.list_eyebrow && <p className="eyebrow mb-7">{d.list_eyebrow}</p>}
          <Items d={d} />
          {d.note && <p className="mt-10 text-[14px] font-light text-stoneish border-l border-oro/50 pl-5">{d.note}</p>}
        </Reveal>
      </div>
    </section>
  );
};

export const Quote = ({ d }) => (
  <section className={`${bg(d)} py-24 sm:py-32`} data-testid="block-quote"><div className="container-x grid grid-cols-12">
    <Reveal className="col-span-12 lg:col-span-9 lg:col-start-2"><figure>
      <span className="block font-serif text-[80px] leading-[0.5] text-oro/60 mb-6" aria-hidden="true">“</span>
      <blockquote className="font-serif font-light italic text-3xl sm:text-[46px] leading-[1.28] tracking-[-0.02em] text-ink">{d.text}</blockquote>
      {d.author && <figcaption className="mt-8 flex items-center gap-5 text-[12.5px] uppercase tracking-[0.16em] text-stoneish"><span className="w-10 h-px bg-oro" />{d.author}</figcaption>}
    </figure></Reveal>
  </div></section>
);

export const Gallery = ({ d }) => {
  const [idx, setIdx] = useState(null);
  const photos = (d.photos || []).filter((p) => p.src);
  if (!photos.length) return null;
  const grid = d.layout === "grid";
  return (
    <section className="container-x py-24 sm:py-32" data-testid="service-gallery">
      <Head d={d} />
      <div className={grid ? "grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6" : "columns-2 lg:columns-3 gap-3 sm:gap-6 [&>*]:mb-3 sm:[&>*]:mb-6"}>
        {photos.map((p, k) => <button key={p.src + k} onClick={() => setIdx(k)} className={`block w-full overflow-hidden img-zoom break-inside-avoid cursor-zoom-in ${grid ? "aspect-square" : ""} ${!grid && k % 3 === 1 ? "lg:mt-8" : ""}`} aria-label={`Apri fotografia ${k + 1}`}><Img src={p.src} alt={p.alt} className={grid ? "w-full h-full object-cover" : "w-full h-auto"} /></button>)}
      </div>
      <Lightbox photos={photos.map((p) => p.src)} index={idx} onClose={() => setIdx(null)} onIndex={setIdx} />
    </section>
  );
};
